import React, { useState, useEffect } from 'react';
import { generateClient } from 'aws-amplify/data';
import MemberNav from './MemberNav';
import { computeSeasonStandings, SeasonStandingEntry } from '../../utils/championPoints';
const client = generateClient({ authMode: 'userPool' });

interface SeasonChampionshipPageProps {
  onNavigate: (page: 'dashboard' | 'profile' | 'competitions' | 'catches' | 'my-stats' | 'my-fishing' | 'season-championship' | 'home') => void;
}

const SeasonChampionshipPage: React.FC<SeasonChampionshipPageProps> = ({ onNavigate }) => {
  const [seniorStandings, setSeniorStandings] = useState<SeasonStandingEntry[]>([]);
  const [juniorStandings, setJuniorStandings] = useState<SeasonStandingEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const year = new Date().getFullYear();

  useEffect(() => {
    (async () => {
      try {
        // #134: no server-side stored competition state filter — page fully and let
        // computeSeasonStandings filter client-side with isPublished.
        let allComps: any[] = [];
        let nextToken: string | null = null;
        do {
          const result: any = await client.models.Event.list({
            filter: { eventType: { eq: 'COMPETITION' } },
            selectionSet: ['id', 'eventDate', 'publishedAt', 'seniorChampionName', 'seniorChampionPoints', 'seniorChampionMemberSub', 'juniorChampionName', 'juniorChampionPoints', 'juniorChampionMemberSub'],
            ...(nextToken ? { nextToken } : {}),
          });
          allComps = allComps.concat(result.data || []);
          nextToken = result.nextToken ?? null;
        } while (nextToken);
        const result = computeSeasonStandings(allComps, year);
        setSeniorStandings(result.seniorStandings);
        setJuniorStandings(result.juniorStandings);
      } catch {
        setError('Failed to load championship standings');
      } finally {
        setLoading(false);
      }
    })();
  }, [year]);

  const renderTable = (label: string, standings: SeasonStandingEntry[]) => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 mb-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">{label}</h2>
      {standings.length === 0 ? (
        <p className="text-sm text-gray-500 italic">No results yet for {year} season</p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-left text-gray-500">
              <th className="py-2 w-12">Rank</th>
              <th className="py-2">Name</th>
              <th className="py-2 text-right">Points</th>
              <th className="py-2 text-right">Comps</th>
            </tr>
          </thead>
          <tbody>
            {standings.slice(0, 10).map((s, i) => (
              <tr key={s.memberSub} className="border-b last:border-b-0">
                <td className="py-2 font-medium text-gray-600">{s.rank}</td>
                <td className="py-2">{s.competitorName}</td>
                <td className="py-2 text-right font-bold text-fishing-blue">{s.totalPoints}</td>
                <td className="py-2 text-right text-gray-600">{s.competitionsCount}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <MemberNav currentPage={"dashboard" as any} onNavigate={onNavigate} />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">🏅 {year} Season Championship</h1>
        {loading && <p className="text-gray-600">Loading standings...</p>}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
            <p className="text-red-800">{error}</p>
          </div>
        )}
        {!loading && !error && (
          <>
            {renderTable('Senior Division', seniorStandings)}
            {renderTable('Junior Division', juniorStandings)}
          </>
        )}
      </div>
    </div>
  );
};

export default SeasonChampionshipPage;
